import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  debounce,
  noop,
  find,
  flatten,
  groupBy,
  sortBy,
  upperFirst,
} from 'lodash';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { connect } from 'react-redux';
import Chip from '@material-ui/core/Chip';
import {
  createMuiTheme,
  MuiThemeProvider,
  withStyles,
} from '@material-ui/core/styles';

import { HighlightText, injectIntl } from 'components';
import { selectState } from 'redux/selectors';
import SelectField from 'components/SmartForm/components/SelectField';

const styles = (theme) => {
  const themeStatus = theme.status ? theme.status : { tag: {}, approved: false };
  return ({
    chip: {
      backgroundColor: themeStatus.tag.backgroundColor || 'transparent',
      height: 20,
      marginBottom: 6,
      marginRight: 2,
      marginTop: 6,
    },
    chipColor: { color: themeStatus.approved ? (themeStatus.tag.color || 'black') : '#808080' },
    chipDeleteIcon: {
      color: themeStatus.tag.backgroundColor || 'gray',
      fill: themeStatus.tag.color || 'darkgray',
    },
    chipLarge: {},
    chipMedium: {
      color: '#008000',
    },
    chipSmall: {
      borderRadius: 3,
      color: '#008000',
      fontSize: 10,
      fontWeight: 400,
      height: 20,
      lineHeight: '20px',
      paddingLeft: 5,
      paddingRight: 5,
    },
    label: {
      fontSize: 13,
      transform: 'translate(14px, 4px) scale(1);',
    },
    selectLabel: {
      fontSize: 15,
      padding: '6px 46px 7px 0',
    },
    selectMenu: {
      padding: 0,
      paddingBottom: 5,
      paddingTop: 5,
    },
    selectTag: {
      height: 25,
      marginTop: 10,
      width: 110,
    },
  });
};

class Tags extends Component {
  static propTypes = {
    approved: PropTypes.bool,
    classes: PropTypes.shape({}).isRequired,
    defaultTags: PropTypes.arrayOf(PropTypes.string),
    highlightText: PropTypes.string,
    removable: PropTypes.bool,
    showDropdown: PropTypes.bool,
    size: PropTypes.oneOf(['small', 'medium', 'large']),
    tags: ImmutablePropTypes.listOf(
      ImmutablePropTypes.mapContains({
        category: PropTypes.string,
        name: PropTypes.string,
        description: PropTypes.string,
      }),
    ).isRequired,

    formatMessage: PropTypes.func.isRequired,
    onChange: PropTypes.func,
  };

  static defaultProps = {
    approved: true,
    defaultTags: [],
    highlightText: '',
    removable: true,
    showDropdown: true,
    size: 'medium',

    onChange: noop,
  };

  constructor(props) {
    super(props);
    const tagsList = this.getTagsList(props);
    const currentTags = props.defaultTags.map(tag =>
      flatten(tagsList).find(option => option.name.toLowerCase() === tag.toLowerCase()) || {},
    );

    this.state = { currentTags };
    this.debouncedOnAddTag = debounce(this.onAddTag, 350);
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.defaultTags !== this.props.defaultTags) {
      const tagsList = this.getTagsList(nextProps);
      const currentTags = nextProps.defaultTags.map(tag =>
        flatten(tagsList).find(option => option.name.toLowerCase() === tag.toLowerCase()) || {},
      );
      this.setState({ currentTags });
    }
  }

  onAddTag = (tagId) => {
    const options = this.getOptions();
    let currentTags = this.state.currentTags.slice();
    currentTags.push(find(options, ['id', parseInt(tagId, 10)]));
    currentTags = sortBy(currentTags, ['category', 'name']);
    this.onChangeTags(currentTags);
  }

  onChangeTags = (currentTags) => {
    const { onChange } = this.props;

    this.setState({ currentTags });
    onChange(currentTags.map(tag => tag.name));
  }

  onRequestDelete = (tagId) => {
    const tagIndex = this.state.currentTags.findIndex(tag => tag.id === tagId);
    const currentTags = this.state.currentTags.slice();

    currentTags.splice(tagIndex, 1);
    this.onChangeTags(currentTags);
  }

  getOptions = () => {
    const tagsList = this.getTagsList(this.props);
    const { currentTags } = this.state;
    const tagNames = currentTags.map(tag => tag.name);
    const filteredOptions = tagsList.filter(options =>
      !options.find(option =>
        tagNames.find(tagName => option.name === tagName),
      ),
    );

    const result = flatten(filteredOptions).map(option => ({
      ...option,
      label: option.name,
      value: option.id,
    }));
    result.unshift({
      label: '',
      value: '',
    });

    return result;
  }

  getTagsList = ({ tags }) => {
    const groupedTags = groupBy(tags.toJS(), 'category');
    const groupIds = Object.keys(groupedTags);

    return groupIds.map(groupId => groupedTags[groupId]);
  }

  render() {
    const {
      approved,
      classes,
      removable,
      showDropdown,
      size,
      tags,
      formatMessage,
      highlightText,
    } = this.props;
    const { currentTags } = this.state;
    const options = this.getOptions();

    return (
      <div className="form-field">
        {!!options.length && showDropdown && removable && (
          <SelectField
            fullWidth
            classes={{
              label: { root: classes.label },
              root: { root: classes.selectTag },
              select: {
                select: classes.selectLabel,
                selectMenu: classes.selectMenu,
              },
            }}
            label={`${formatMessage('Tags')}`}
            nativeSelect
            onChange={this.debouncedOnAddTag}
            options={options}
            value={0}
            variant
          />
        )}
        <div className="row" style={{ marginRight: 0, marginLeft: 0, marginTop: 6, zIndex: 1 }}>
          {tags.size ? currentTags.map((tag, index) => {
            const tagTheme = createMuiTheme({
              status: { tag, approved },
              typography: {
                useNextVariants: true,
              },
            });
            return (
              <MuiThemeProvider key={index} theme={tagTheme}>
                <Chip
                  classes={{
                    deleteIcon: classes.chipDeleteIcon,
                    label: `${classes[`chip${upperFirst(size)}`]} ${classes.chipColor}`,
                    root: classes.chip,
                  }}
                  label={
                    highlightText ? (
                      <HighlightText
                        highlight={highlightText}
                        text={tag.name}
                      />
                    ) : tag.name
                  }
                  onDelete={removable ? () => this.onRequestDelete(tag.id) : null}
                />
              </MuiThemeProvider>
            );
          }) : null}
        </div>
      </div>
    );
  }
}

/* istanbul ignore next */
const mapStateToProps = state => ({
  ...selectState('options', 'tags')(state, 'tags'),
});

const wrappedTags = connect(mapStateToProps)(withStyles(styles)(injectIntl(Tags)));
wrappedTags.displayName = 'Tags';

export default wrappedTags;
